import { IconButton } from '@creditornot/cb-button';
import { FormRow, FormRowProps } from '@creditornot/cb-form';
import { ChainLink, DocumentEditSimple } from '@creditornot/cb-icons';
import React, { CSSProperties, ReactNode, useCallback, useMemo, useState } from 'react';
import { useForm, useFormState } from 'react-final-form';
import styled from 'styled-components';

import { useSelectionData } from 'modules/shared-menu/hooks/useSelectionData';

import { transformFeedProductToFormValues } from './transformers';
import { ProductFormValues, FormValues } from './types';
import { useProductOverridesContext } from '../../context/ProductOverridesContext';

export type WithCatalogLinkFormRowProps = Omit<FormRowProps, 'label'> & {
  attrName: keyof ProductFormValues;
  children: ReactNode;
  renderFormRow?: boolean;
  label?: string;
  editIconStyle?: CSSProperties;
};

const StyledRightCell = styled.div`
  display: flex;
`;

const ChildrenContainer = styled.div`
  flex: 1;
`;

const StyledIconButton = styled(IconButton)`
  margin-block-start: auto;
  margin-inline-start: 8px;
`;

export const WithCatalogLinkFormRow = ({
  attrName,
  children,
  editIconStyle,
  label = '',
  renderFormRow = true,
  ...rest
}: WithCatalogLinkFormRowProps) => {
  const form = useForm<FormValues>();
  const { values } = useFormState<FormValues>();
  const { selection } = useSelectionData();
  const [userValue, setUserValue] = useState<any>(values[attrName]);
  const {
    connectedProduct,
    currOverriddenFormFields,
    connectProductAttr,
    overrideProductAttr,
    productInfoSettings,
    connectedProductId,
  } = useProductOverridesContext();
  const isConnected = useMemo(() => !currOverriddenFormFields.includes(attrName), [attrName, currOverriddenFormFields]);
  const productFormValues = useMemo(() => {
    if (!connectedProduct || !productInfoSettings?.nutrition_values) {
      return null;
    }
    return transformFeedProductToFormValues(connectedProduct, {
      language: selection.primary_language,
      nutritionSettings: productInfoSettings?.nutrition_values,
    });
  }, [connectedProduct, productInfoSettings?.nutrition_values, selection.primary_language]);

  const overrideFormField = useCallback(() => {
    overrideProductAttr(attrName);
    form.change(attrName, userValue);
  }, [overrideProductAttr, attrName, form, userValue]);

  const handleReconnectCatalog = useCallback(() => {
    if (!productFormValues) {
      return;
    }
    connectProductAttr(attrName);
    setUserValue(values[attrName]);
    form.change(attrName, productFormValues[attrName]);
  }, [connectProductAttr, form, attrName, productFormValues, values]);

  const Content = useMemo(
    () => (
      <StyledRightCell>
        <ChildrenContainer>{children}</ChildrenContainer>
        {connectedProductId && (
          <div style={editIconStyle}>
            <StyledIconButton
              size="tiny"
              variant="blue"
              background="animated"
              onClick={isConnected ? overrideFormField : handleReconnectCatalog}
            >
              {isConnected ? <DocumentEditSimple /> : <ChainLink />}
            </StyledIconButton>
          </div>
        )}
      </StyledRightCell>
    ),
    [children, connectedProductId, editIconStyle, handleReconnectCatalog, isConnected, overrideFormField],
  );

  if (renderFormRow) {
    return (
      <FormRow label={label} {...rest}>
        {Content}
      </FormRow>
    );
  }

  return Content;
};
